// src/cockpit4/components/AgentMeshConsole.tsx
// ============================================
// Agent Mesh 管控台 — 让后台的"经验飞轮 + Agent 编排层"在面板里可见、可管理
//
// 功能（路由/编排零 token）：
//   1. 两个 Runtime 状态卡 + 启用开关（Experience / AgentMesh）
//   2. 经验飞轮统计（已记录结果 / 已沉淀经验卡 / Vault 卡片数）
//   3. Agent 能力目录（AI 超市能力卡）
//   4. 意图路由试算（输入目标 → 看路由到哪些 Agent）
//   5. 运行编排（P2 深化：plan 预览 → run_swarm 真并行执行 → aggregated 展示）
//
// P2 已开放：config.enabled=true，路由/计划零 token；真执行（swarm）接 MediaStudio/Knowledge。
// ============================================

import { useEffect, useState } from "react";
import {
  Boxes, GitBranch, Database, Power, RefreshCw,
  Search, Loader2, Cpu, Layers, ShieldCheck, Bot, Route as RouteIcon, Sparkles,
  Play, ListChecks, CheckCircle2, XCircle,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

interface ExpState {
  enabled: boolean;
  recordedResults: number;
  derivedCards: number;
  vaultCards: number;
}
interface AgentDef {
  id: string;
  name: string;
  role: string;
  description: string;
  capabilities: string[];
  enabled: boolean;
}
interface RouteDecision {
  agentId: string;
  agentName: string;
  role: string;
  score: number;
  reason: string;
}
interface MeshState {
  enabled: boolean;
  catalogCount: number;
  routeTopK: number;
  maxConcurrency: number;
  defaultParallel: boolean;
}
interface SwarmPlan {
  planId: string;
  goal: string;
  agents: AgentDef[];
  parallel: boolean;
  createdAt: string;
}
interface SwarmAgentResult {
  agentId: string;
  agentName: string;
  output: string;
  ok: boolean;
}
interface SwarmResult {
  planId: string;
  goal: string;
  results: SwarmAgentResult[];
  aggregated: string;
  createdAt: string;
}

/** 解包 RuntimeManager 双层 data（{data:{data}}） */
function unpack<T>(resp: any): T | undefined {
  return (resp?.data?.data ?? resp?.data) as T | undefined;
}

const ROLE_LABEL: Record<string, string> = {
  planner: "规划", researcher: "研究", writer: "写作",
  reviewer: "审阅", analyst: "分析", executor: "执行", quality: "质检",
};

export default function AgentMeshConsole() {
  const [exp, setExp] = useState<ExpState | null>(null);
  const [catalog, setCatalog] = useState<AgentDef[]>([]);
  const [mesh, setMesh] = useState<MeshState | null>(null);
  const [route, setRoute] = useState<RouteDecision[] | null>(null);
  const [goal, setGoal] = useState("把这个热点做成五个平台的内容");
  const [routing, setRouting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");
  // P2 深化：运行编排（plan 预览 → run_swarm → aggregated）
  const [runGoal, setRunGoal] = useState("把今天的热点做成小红书图文和抖音口播脚本");
  const [plan, setPlan] = useState<{ decisions: RouteDecision[]; plan: SwarmPlan } | null>(null);
  const [swarm, setSwarm] = useState<SwarmResult | null>(null);
  const [planning, setPlanning] = useState(false);
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setBusy(true);
    try {
      const [e, c, m] = await Promise.all([
        RuntimeManager.query(RuntimeName.Experience, { type: "state" }).catch(() => null),
        RuntimeManager.query(RuntimeName.AgentMesh, { type: "list_catalog" }).catch(() => null),
        RuntimeManager.query(RuntimeName.AgentMesh, { type: "state" }).catch(() => null),
      ]);
      setExp(unpack<ExpState>(e) ?? null);
      const cat = unpack<{ catalog: AgentDef[] }>(c);
      setCatalog(cat?.catalog ?? []);
      setMesh(unpack<MeshState>(m) ?? null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async (which: "experience" | "agentmesh", to: boolean) => {
    setMsg("");
    try {
      const name = which === "experience" ? RuntimeName.Experience : RuntimeName.AgentMesh;
      const resp = await RuntimeManager.execute(name, "set_enabled", { enabled: to });
      setMsg(resp?.success !== false ? `已${to ? "开启" : "停用"} ${which}` : `操作失败：${resp?.error ?? ""}`);
      refresh();
    } catch (err: any) {
      setMsg(`操作出错：${err?.message ?? String(err)}`);
    }
  };

  const runRoute = async () => {
    if (!goal.trim()) return;
    setRouting(true);
    try {
      const resp = await RuntimeManager.query(RuntimeName.AgentMesh, { type: "route", goal, topK: 6 });
      const d = unpack<{ decisions: RouteDecision[] }>(resp);
      setRoute(d?.decisions ?? []);
    } catch (err: any) {
      setRoute([]);
      setMsg(`路由试算出错：${err?.message ?? String(err)}`);
    } finally {
      setRouting(false);
    }
  };

  // ============ P2 深化：运行编排（plan 预览零 token → run_swarm 真并行） ============
  const makePlan = async () => {
    if (!runGoal.trim()) return;
    setPlanning(true);
    setSwarm(null);
    setMsg("");
    try {
      const resp = await RuntimeManager.execute(RuntimeName.AgentMesh, "plan", { goal: runGoal.trim(), topK: 6, parallel: true });
      const d = unpack<{ decisions: RouteDecision[]; plan: SwarmPlan }>(resp);
      setPlan(d ?? null);
    } catch (err: any) {
      setPlan(null);
      setMsg(`生成编排计划出错：${err?.message ?? String(err)}`);
    } finally {
      setPlanning(false);
    }
  };

  const runSwarm = async () => {
    if (!runGoal.trim()) return;
    setRunning(true);
    setMsg("");
    try {
      const resp = await RuntimeManager.execute(RuntimeName.AgentMesh, "run_swarm", { goal: runGoal.trim(), topK: 6, parallel: true });
      const d = unpack<SwarmResult>(resp);
      setSwarm(d ?? null);
    } catch (err: any) {
      setSwarm(null);
      setMsg(`运行编排出错：${err?.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  // ============ 飞轮入口：记一条效果 ============
  const [fx, setFx] = useState({ topic: "", platform: "小红书", views: "", likes: "", leads: "", deals: "" });
  const [savingFx, setSavingFx] = useState(false);

  const submitExperience = async () => {
    if (!fx.topic.trim()) { setMsg("请填写主题/选题"); return; }
    setSavingFx(true);
    setMsg("");
    try {
      const num = (v: string) => { const n = parseInt(v || "0", 10); return Number.isFinite(n) ? n : 0; };
      const resp = await RuntimeManager.execute(RuntimeName.Experience, "record_and_derive", {
        topic: fx.topic.trim(),
        platform: fx.platform,
        metrics: {
          views: num(fx.views), likes: num(fx.likes),
          leads: num(fx.leads), deals: num(fx.deals),
        },
      });
      const inner = resp?.data as any;
      const grade = inner?.card?.grade;
      setMsg(grade ? `✅ 已记录并沉淀经验卡 [${grade}]：${fx.topic}@${fx.platform}` : `已写入，${resp?.error ?? "请到 Obsidian 查看经验卡"}`);
      refresh();
    } catch (err: any) {
      setMsg(`记效果出错：${err?.message ?? String(err)}`);
    } finally {
      setSavingFx(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-[520px]">
      {/* 头部 */}
      <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Boxes size={16} className="text-purple-400" />
          <span className="text-[13px] font-semibold text-white">Agent Mesh 管控台</span>
          <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">企业 Agent 编排层</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono text-gray-500">P2 已开放 · 路由/编排零 token</span>
          <button onClick={refresh} className="p-1.5 rounded-md border border-white/[0.08] text-gray-400 hover:text-white hover:bg-white/[0.05]">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {msg && (
        <div className="px-4 py-1.5 border-b border-purple-500/20 bg-purple-500/10 text-[11px] text-purple-200 font-mono">
          {msg}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* 1. 两个 Runtime 状态卡 */}
        <div className="grid grid-cols-2 gap-3">
          <StatusCard
            icon={<Database size={14} className="text-emerald-400" />}
            title="经验飞轮 Experience"
            enabled={exp?.enabled ?? false}
            onToggle={(v) => toggle("experience", v)}
            lines={[
              `已记录结果 ${exp?.recordedResults ?? 0}`,
              `已沉淀经验卡 ${exp?.derivedCards ?? 0}`,
              `Vault 卡片 ${exp?.vaultCards ?? 0}`,
            ]}
          />
          <StatusCard
            icon={<Cpu size={14} className="text-purple-400" />}
            title="Agent 编排 AgentMesh"
            enabled={mesh?.enabled}
            onToggle={(v) => toggle("agentmesh", v)}
            lines={[
              `能力目录 ${mesh?.catalogCount ?? catalog.length} 张`,
              `并行上限 ${mesh?.maxConcurrency ?? "-"} · 默认并行 ${mesh?.defaultParallel ? "开" : "关"}`,
              "invoker 已接线（MediaStudio/Knowledge）",
            ]}
          />
        </div>

        {/* 1.5 飞轮入口：记一条效果（数据回流到经验库） */}
        <div>
          <SectionTitle icon={<Database size={13} className="text-emerald-400" />} title="飞轮入口 · 记一条真实效果（写回 Obsidian）" />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input
              value={fx.topic}
              onChange={(e) => setFx({ ...fx, topic: e.target.value })}
              placeholder="主题/选题（如 酒店AI降本）"
              className="col-span-2 px-3 py-1.5 rounded-lg bg-[#0a0e18] border border-white/[0.1] text-[12px] text-gray-200 focus:outline-none focus:border-emerald-500/40"
            />
            <select
              value={fx.platform}
              onChange={(e) => setFx({ ...fx, platform: e.target.value })}
              className="px-2 py-1.5 rounded-lg bg-[#0a0e18] border border-white/[0.1] text-[12px] text-gray-200 focus:outline-none"
            >
              {["小红书", "抖音", "微信公众号", "视频号", "知乎", "B站"].map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div className="grid grid-cols-4 gap-1.5">
              {([
                { k: "views", label: "播放" },
                { k: "likes", label: "点赞" },
                { k: "leads", label: "咨询" },
                { k: "deals", label: "成交" },
              ] as const).map((f) => (
                <input
                  key={f.k}
                  type="number"
                  value={fx[f.k]}
                  onChange={(e) => setFx({ ...fx, [f.k]: e.target.value })}
                  placeholder={f.label}
                  className="px-2 py-1.5 rounded-lg bg-[#0a0e18] border border-white/[0.1] text-[12px] text-gray-200 focus:outline-none focus:border-emerald-500/40"
                />
              ))}
            </div>
            <button
              onClick={submitExperience}
              disabled={savingFx || !fx.topic.trim()}
              className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[11px] hover:bg-emerald-500/25 disabled:opacity-40"
            >
              {savingFx ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              记录并提炼经验卡
            </button>
          </div>
          <div className="mt-1 text-[10px] text-gray-500">真实效果 → 规则提炼评级(S/A/B/C/D) → 自动写回 Obsidian，供下次内容生产复用</div>
        </div>

        {/* 2. Agent 能力目录 */}
        <div>
          <SectionTitle icon={<Layers size={13} className="text-purple-400" />} title="Agent 能力目录（AI 超市能力卡）" />
          <div className="grid grid-cols-2 gap-2 mt-2">
            {catalog.map((a) => (
              <div key={a.id} className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-white">{a.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300">
                    {ROLE_LABEL[a.role] ?? a.role}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] text-gray-500 line-clamp-2">{a.description}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {a.capabilities.slice(0, 4).map((c) => (
                    <span key={c} className="text-[9px] font-mono px-1 py-0.5 rounded bg-white/[0.04] text-gray-400">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>



        {/* 3. 意图路由试算 */}
        <div>
          <SectionTitle icon={<RouteIcon size={13} className="text-purple-400" />} title="意图路由试算（零 token）" />
          <div className="mt-2 flex gap-2">
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runRoute()}
              className="flex-1 px-3 py-1.5 rounded-lg bg-[#0a0e18] border border-white/[0.1] text-[12px] text-gray-200 focus:outline-none focus:border-purple-500/40"
              placeholder="输入一个目标，看会路由到哪些 Agent..."
            />
            <button
              onClick={runRoute}
              disabled={routing || !goal.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-200 text-[11px] hover:bg-purple-500/30 disabled:opacity-40"
            >
              {routing ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              试算路由
            </button>
          </div>
          {route && (
            <div className="mt-2 space-y-1">
              {route.length === 0 && <div className="text-[11px] text-gray-500">未命中任何 Agent（试试含"小红书/抖音/写作/调研"的目标）</div>}
              {route.map((d) => (
                <div key={d.agentId} className="flex items-center justify-between px-3 py-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
                  <span className="flex items-center gap-2 text-[11px] text-gray-200">
                    <Bot size={11} className="text-purple-400" />
                    {d.agentName}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="text-[9px] font-mono text-gray-500">{d.reason}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">分 {d.score}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 4. P2 深化：运行编排（plan 预览 → run_swarm 真并行 → aggregated） */}
        <div>
          <SectionTitle icon={<Play size={13} className="text-emerald-400" />} title="运行编排 · 真并行 Swarm" />
          <div className="mt-2 space-y-2">
            <div className="flex gap-2">
              <input
                value={runGoal}
                onChange={(e) => setRunGoal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && makePlan()}
                className="flex-1 px-3 py-1.5 rounded-lg bg-[#0a0e18] border border-white/[0.1] text-[12px] text-gray-200 focus:outline-none focus:border-emerald-500/40"
                placeholder="输入一个创作/运营目标，预览编排计划并真执行..."
              />
              <button
                onClick={makePlan}
                disabled={planning || !runGoal.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/15 border border-cyan-500/30 text-cyan-200 text-[11px] hover:bg-cyan-500/25 disabled:opacity-40"
              >
                {planning ? <Loader2 size={12} className="animate-spin" /> : <ListChecks size={12} />}
                预览计划
              </button>
              <button
                onClick={runSwarm}
                disabled={running || !runGoal.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[11px] hover:bg-emerald-500/25 disabled:opacity-40"
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                执行编排
              </button>
            </div>
            <div className="text-[10px] text-gray-500">Plan 零 token 只出方案；执行走 AgentMesh invoker → 真调 MediaStudio/Knowledge 产出。</div>
          </div>

          {/* Plan 预览 */}
          {plan && (
            <div className="mt-2 px-3 py-2 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.04]">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono text-cyan-300">计划 {plan.plan.planId.slice(0, 8)} · {plan.plan.agents.length} 个 Agent {plan.plan.parallel ? "（并行）" : "（串行）"}</span>
                <span className="text-[9px] font-mono text-gray-500">{plan.plan.createdAt}</span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {plan.plan.agents.map((a) => (
                  <span key={a.id} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-200 border border-cyan-500/20">
                    {a.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Swarm 结果 */}
          {swarm && (
            <div className="mt-2 space-y-2">
              <div className="px-3 py-2 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04]">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-emerald-300">执行完成 {swarm.results.filter((r) => r.ok).length}/{swarm.results.length} · {swarm.planId.slice(0, 8)}</span>
                  <span className="text-[9px] font-mono text-gray-500">{swarm.createdAt}</span>
                </div>
                <div className="mt-1.5 space-y-1">
                  {swarm.results.map((r) => (
                    <div key={r.agentId} className="flex items-start gap-1.5">
                      {r.ok ? <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 flex-shrink-0" /> : <XCircle size={11} className="text-red-400 mt-0.5 flex-shrink-0" />}
                      <span className="text-[10px] font-mono text-gray-400 leading-relaxed">
                        <span className="text-gray-200">{r.agentName}</span>：{r.output.slice(0, 220)}{r.output.length > 220 ? "…" : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.02]">
                <div className="flex items-center gap-1.5 mb-1">
                  <GitBranch size={11} className="text-emerald-400" />
                  <span className="text-[10px] font-mono text-emerald-300">聚合输出（供 Hermes 返回）</span>
                </div>
                <pre className="text-[10px] font-mono text-gray-400 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">{swarm.aggregated}</pre>
              </div>
            </div>
          )}
        </div>

        {/* 说明 */}
        <div className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-500 leading-relaxed">
          <GitBranch size={11} className="inline text-purple-400 mr-1" />
          Hermes(COO) → Agent Mesh(员工) → Runtime Layer(部门) → Obsidian(大脑)。
          P2 已开放：路由/计划零 token；真执行（swarm）由 invoker 接到 MediaStudio/Knowledge。
          <ShieldCheck size={11} className="inline text-emerald-400 ml-1" /> 仍可随时显式停用全局开关。
        </div>
      </div>
    </div>
  );
}


// ============================================
// 子组件
// ============================================

function StatusCard(props: {
  icon: React.ReactNode;
  title: string;
  enabled: boolean | undefined;
  onToggle: (v: boolean) => void;
  lines: string[];
}) {
  const { icon, title, enabled, onToggle, lines } = props;
  return (
    <div className="px-3 py-3 rounded-lg border border-white/[0.07] bg-white/[0.02]">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold text-white">
          {icon}
          {title}
        </span>
        <button
          onClick={() => onToggle(!(enabled ?? false))}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-mono border transition-colors ${
            enabled
              ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
              : "bg-white/[0.04] border-white/[0.1] text-gray-400 hover:text-white"
          }`}
        >
          <Power size={10} />
          {enabled ? "已开启" : "已停用"}
        </button>
      </div>
      <div className="mt-2 space-y-0.5">
        {lines.map((l, i) => (
          <div key={i} className="text-[10px] font-mono text-gray-500">{l}</div>
        ))}
      </div>
    </div>
  );
}

function SectionTitle(props: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center gap-1.5">
      {props.icon}
      <span className="text-[11px] font-semibold text-gray-200 uppercase tracking-wider">{props.title}</span>
    </div>
  );
}
